import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const usd = (v, dp = 6) =>
  v == null ? "—" : `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: dp })}`;
const inr = (v) =>
  v == null ? "—" : `\u20B9${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
const int = (v) => (v == null ? "—" : Number(v).toLocaleString("en-US"));

function OpRows({ title, rows, showPeak }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="border border-zinc-800">
      <div className="bg-zinc-900/60 px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-400">
        {title}
      </div>
      <table className="w-full text-xs">
        <thead className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
          <tr>
            <th className="text-left px-3 py-2">Operation</th>
            <th className="text-right px-3 py-2">Reqs</th>
            <th className="text-right px-3 py-2">In</th>
            <th className="text-right px-3 py-2">Cached</th>
            <th className="text-right px-3 py-2">Out</th>
            <th className="text-right px-3 py-2">Audio min</th>
            {showPeak && <th className="text-right px-3 py-2">Peak / Off</th>}
            <th className="text-right px-3 py-2">USD</th>
            <th className="text-right px-3 py-2">INR</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${title}-${r.operation}`} className="border-t border-zinc-800/60">
              <td className="px-3 py-2 text-zinc-300 font-mono">{r.operation}</td>
              <td className="px-3 py-2 text-right text-zinc-400">{int(r.requests)}</td>
              <td className="px-3 py-2 text-right text-zinc-400">{int(r.input_tokens)}</td>
              <td className="px-3 py-2 text-right text-zinc-400">{int(r.cached_input_tokens)}</td>
              <td className="px-3 py-2 text-right text-zinc-400">{int(r.output_tokens)}</td>
              <td className="px-3 py-2 text-right text-zinc-400">{r.audio_minutes ? r.audio_minutes : "—"}</td>
              {showPeak && (
                <td className="px-3 py-2 text-right text-zinc-400">
                  {r.peak_requests ?? 0} / {r.offpeak_requests ?? 0}
                </td>
              )}
              <td className="px-3 py-2 text-right text-white font-mono">{usd(r.cost_usd)}</td>
              <td className="px-3 py-2 text-right text-emerald-300 font-mono">
                {r.cost_inr ? inr(r.cost_inr) : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function SpendJobDetail({ jobId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data: d } = await api.get(`/admin/spend/jobs/${jobId}`);
        if (alive) setData(d);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Could not load job cost detail");
        onClose();
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [jobId, onClose]);

  const t = data?.totals || {};

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-5xl bg-zinc-950 border-zinc-800 text-white max-h-[88vh] overflow-y-auto"
        data-testid="spend-job-detail-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold tracking-tight">
            Cost detail · <span className="font-mono text-sm text-zinc-400">{jobId}</span>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-16 grid place-items-center">
            <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />
          </div>
        ) : data?.message ? (
          <div
            className="border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-200"
            data-testid="spend-job-detail-unavailable"
          >
            {data.message}
          </div>
        ) : (
          <div className="space-y-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: "Total (USD)", value: usd(t.total_usd), tid: "detail-total-usd" },
                { label: "Total (INR)", value: t.total_inr ? inr(t.total_inr) : "Rate not set", tid: "detail-total-inr" },
                {
                  label: "Cost / minute",
                  value: t.cost_per_minute_usd ? usd(t.cost_per_minute_usd) : "No duration",
                  tid: "detail-cost-per-min",
                },
                { label: "Provider requests", value: int(t.requests), tid: "detail-requests" },
              ].map((c) => (
                <div key={c.label} className="border border-zinc-800 bg-zinc-900/40 p-3" data-testid={c.tid}>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{c.label}</div>
                  <div className="text-lg font-mono text-white mt-1">{c.value}</div>
                </div>
              ))}
            </div>

            <div className="text-[11px] font-mono text-zinc-500 flex flex-wrap gap-x-5 gap-y-1">
              <span>Duration: {data?.job?.duration_minutes ? `${data.job.duration_minutes} min` : "unknown"}</span>
              <span>
                {data?.job?.source_language} → {data?.job?.target_language}
              </span>
              <span>Status: {data?.job?.status || "—"}</span>
              <span>Telemetry: {t.usage_status}</span>
              <span>
                Escalations: {int(t.escalation_requests)}
                {t.escalation_rate_pct != null ? ` (${t.escalation_rate_pct}% of QE segments)` : ""}
              </span>
            </div>

            <OpRows title="Whisper (transcription)" rows={data?.whisper} />
            <OpRows title="DeepSeek" rows={data?.deepseek} showPeak />
            <OpRows title="Gemini (quality evaluation)" rows={data?.gemini} />
            <OpRows title="GPT-5.4" rows={data?.gpt54} />

            <div className="border border-zinc-800">
              <div className="bg-zinc-900/60 px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-400">
                Raw cost events ({data?.events?.length || 0})
              </div>
              <div className="max-h-64 overflow-y-auto">
                <table className="w-full text-[11px]" data-testid="spend-job-events-table">
                  <thead className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 sticky top-0 bg-zinc-950">
                    <tr>
                      <th className="text-left px-3 py-2">Time</th>
                      <th className="text-left px-3 py-2">Model</th>
                      <th className="text-left px-3 py-2">Operation</th>
                      <th className="text-right px-3 py-2">In / Cached / Out</th>
                      <th className="text-right px-3 py-2">USD</th>
                      <th className="text-left px-3 py-2">Period</th>
                      <th className="text-left px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.events || []).map((e) => (
                      <tr key={e.cost_event_id} className="border-t border-zinc-800/60">
                        <td className="px-3 py-1.5 text-zinc-500 font-mono">
                          {new Date(e.created_at).toLocaleTimeString()}
                        </td>
                        <td className="px-3 py-1.5 text-zinc-300 font-mono">{e.model || "—"}</td>
                        <td className="px-3 py-1.5 text-zinc-400 font-mono">{e.operation}</td>
                        <td className="px-3 py-1.5 text-right text-zinc-400 font-mono">
                          {int(e.input_tokens)} / {int(e.cached_input_tokens)} / {int(e.output_tokens)}
                        </td>
                        <td className="px-3 py-1.5 text-right text-white font-mono">{usd(e.cost_usd)}</td>
                        <td className="px-3 py-1.5 text-zinc-500 font-mono">{e.pricing_period}</td>
                        <td className="px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-zinc-400">
                          {e.usage_status}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
